import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { 
    getAllReferralLedger, 
    getReferralStats, 
    deleteReferralLedger,
    completeReferral,
    cancelReferral,
    exportReferralLedger
} from '../../actions/referralLedger';
import { Row, Col, Table, Button, Card, Spinner, Badge, Form, Alert, Modal, Pagination } from "react-bootstrap";

const EMPTY_FILTERS = {
    status: '',
    referralId: '',
    referrerPhone: '',
    refereePhone: '',
    startDate: '',
    endDate: '',
    latest: 'true'
};

const ReferralLedgers = (props) => {
    const location = useLocation();
    const [filters, setFilters] = useState(() => {
        const params = new URLSearchParams(location.search);
        return {
            referrer: params.get('referrer') || '',
            referee: params.get('referee') || '',
            ...EMPTY_FILTERS
        };
    });
    const [exportLoading, setExportLoading] = useState(false);
    const [dateError, setDateError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [actionId, setActionId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        props.getAllReferralLedger({ ...filters, page: currentPage });
        props.getReferralStats();
    }, [currentPage]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const handleFilterChange = (e) => {
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
            setDateError('End date must be after start date');
            return;
        }
        setDateError('');
        setCurrentPage(1);
        props.getAllReferralLedger({ ...filters, page: 1 });
    };

    const handleClearFilters = () => {
        setDateError('');
        setFilters({ referrer: '', referee: '', ...EMPTY_FILTERS });
        setCurrentPage(1);
        props.getAllReferralLedger({ page: 1 });
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            await props.exportReferralLedger(filters);
        } finally {
            setExportLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            setIsSubmitting(true);
            await props.deleteReferralLedger(deleteId);
            setShowDeleteModal(false);
            setDeleteId(null);
            setSuccess('Referral ledger entry deleted successfully!');
        } catch (err) {
            setError('Failed to delete referral ledger entry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleComplete = (id) => {
        setActionId(id);
        setShowCompleteModal(true);
    };

    const confirmComplete = async () => {
        try {
            setIsSubmitting(true);
            await props.completeReferral(actionId, {
                triggerOrder: 'ORD123456', // This should come from a form or be dynamic
                deliveryMethod: 'home_delivery'
            });
            setShowCompleteModal(false);
            setActionId(null);
            setSuccess('Referral completed successfully!');
        } catch (err) {
            setError('Failed to complete referral. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = (id) => {
        setActionId(id);
        setShowCancelModal(true);
    };

    const confirmCancel = async () => {
        try {
            setIsSubmitting(true);
            await props.cancelReferral(actionId, {
                reason: 'Admin cancelled referral'
            });
            setShowCancelModal(false);
            setActionId(null);
            setSuccess('Referral cancelled successfully!');
        } catch (err) {
            setError('Failed to cancel referral. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { variant: 'warning', text: 'Pending' },
            completed: { variant: 'success', text: 'Completed' },
            cancelled: { variant: 'danger', text: 'Cancelled' },
            expired: { variant: 'secondary', text: 'Expired' }
        };
        
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderStats = () => {
        if (!props.stats) return null;

        return (
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center border-primary">
                        <Card.Body>
                            <h3 className="text-primary">{props.stats.totalReferrals}</h3>
                            <p className="text-muted mb-0">Total Referrals</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-success">
                        <Card.Body>
                            <h3 className="text-success">{props.stats.completedReferrals}</h3>
                            <p className="text-muted mb-0">Completed</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-warning">
                        <Card.Body>
                            <h3 className="text-warning">{props.stats.pendingReferrals}</h3>
                            <p className="text-muted mb-0">Pending</p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-info">
                        <Card.Body>
                            <h3 className="text-info">{props.stats.totalRewardCans}</h3>
                            <p className="text-muted mb-0">Reward Cans</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderFilters = () => {
        return (
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    {(filters.referrer || filters.referee) && (
                        <Alert variant="info" className="py-2">
                            Showing referrals for one user only. Use Clear to see all entries.
                        </Alert>
                    )}
                    <Form onSubmit={handleFilterSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-ticket-alt me-2"></i>
                                        Referral ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="referralId"
                                        value={filters.referralId}
                                        onChange={handleFilterChange}
                                        placeholder="Enter referral ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-phone me-2"></i>
                                        Referrer Phone
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="referrerPhone"
                                        value={filters.referrerPhone}
                                        onChange={handleFilterChange}
                                        placeholder="Enter referrer phone number"
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-phone me-2"></i>
                                        Referee Phone
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="refereePhone"
                                        value={filters.refereePhone}
                                        onChange={handleFilterChange}
                                        placeholder="Enter referee phone number"
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-exclamation-circle me-2"></i>
                                        Status
                                    </Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="expired">Expired</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                        isInvalid={!!dateError}
                                    />
                                    <Form.Control.Feedback type="invalid">{dateError}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={filters.latest}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <div className="d-flex gap-2 w-100">
                                    <Button type="submit" variant="primary" disabled={props.loading} className="flex-fill">
                                        <i className="fas fa-search me-2"></i>
                                        Apply Filters
                                    </Button>
                                    <Button type="button" variant="outline-secondary" onClick={handleClearFilters} disabled={props.loading}>
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    const renderPagination = () => {
        const { pagination } = props;
        if (!pagination || pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === pagination.page}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={pagination.pages} onClick={() => handlePageChange(pagination.pages)}>
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={pagination.page === 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={pagination.page === pagination.pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    if (props.loading) {
        return (
            <div className="d-flex justify-content-center align-items-center loading-container" style={{minHeight: '400px'}}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading referral ledger...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="text-primary mb-1">
                            <i className="fas fa-handshake me-2"></i>
                            Referral Ledger
                        </h2>
                        <p className="text-muted mb-0">
                            Manage and track referral program entries
                        </p>
                    </div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleExport}
                        disabled={exportLoading}
                        className="d-flex align-items-center"
                    >
                        {exportLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-download me-1"></i>
                                Export CSV
                            </>
                        )}
                    </Button>
                </div>

                {/* Alert Messages */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="fas fa-check-circle me-2"></i>
                        {success}
                    </Alert>
                )}

                {/* Statistics */}
                {renderStats()}

                {/* Filters */}
                {renderFilters()}

                {/* Referral Ledger Table */}
                <Card className="shadow-sm">
                    <Card.Header className="bg-primary text-white">
                        <h5 className="mb-0">
                            <i className="fas fa-list me-2"></i>
                            Referral Ledger Entries
                        </h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table striped hover responsive className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Referral ID</th>
                                    <th>Referrer</th>
                                    <th>Referee</th>
                                    <th>Status</th>
                                    <th>Reward</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.referralLedger && props.referralLedger.length > 0 ? (
                                    props.referralLedger.map((entry) => (
                                        <tr key={entry._id}>
                                            <td>
                                                <code>{entry.referralId}</code>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{entry.referrer?.name || 'N/A'}</div>
                                                    <small className="text-muted">{entry.referrer?.phone || 'N/A'}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{entry.referee?.name || 'N/A'}</div>
                                                    <small className="text-muted">{entry.referee?.phone || 'N/A'}</small>
                                                </div>
                                            </td>
                                            <td>
                                                {getStatusBadge(entry.status)}
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-bold">{entry.reward?.totalValue || 0} cans</div>
                                                    <small className="text-muted">
                                                        R: {entry.reward?.referrerCans || 0} | 
                                                        E: {entry.reward?.refereeCans || 0}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div>{formatDate(entry.createdAt)}</div>
                                                    {entry.expiresAt && (
                                                        <small className="text-muted">
                                                            Expires: {formatDate(entry.expiresAt)}
                                                        </small>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {entry.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="success"
                                                                onClick={() => handleComplete(entry._id)}
                                                                title="Complete Referral"
                                                            >
                                                                <i className="fas fa-check"></i>
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="danger"
                                                                onClick={() => handleCancel(entry._id)}
                                                                title="Cancel Referral"
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </Button>
                                                        </>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleDelete(entry._id)}
                                                        title="Delete Entry"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4">
                                            <i className="fas fa-handshake fa-3x text-muted mb-3"></i>
                                            <p className="text-muted">No referral ledger entries found.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* Pagination */}
                {renderPagination()}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to delete this referral ledger entry?</h5>
                    <p className="text-muted mb-0">
                        This action cannot be undone. The entry will be permanently removed from the system.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={confirmDelete}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-trash me-2"></i>
                                Delete Entry
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Complete Referral Modal */}
            <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>
                        <i className="fas fa-check-circle me-2"></i>
                        Complete Referral
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-handshake fa-3x text-success mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to complete this referral?</h5>
                    <p className="text-muted mb-0">
                        This will mark the referral as completed and process the rewards.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowCompleteModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={confirmComplete}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Completing...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check me-2"></i>
                                Complete Referral
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Referral Modal */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                <Modal.Header closeButton className="bg-warning text-dark">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Cancel Referral
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-times-circle fa-3x text-warning mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to cancel this referral?</h5>
                    <p className="text-muted mb-0">
                        This will mark the referral as cancelled and no rewards will be processed.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowCancelModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="warning" 
                        onClick={confirmCancel}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Cancelling...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-times me-2"></i>
                                Cancel Referral
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

ReferralLedgers.propTypes = {
    getAllReferralLedger: PropTypes.func.isRequired,
    getReferralStats: PropTypes.func.isRequired,
    deleteReferralLedger: PropTypes.func.isRequired,
    completeReferral: PropTypes.func.isRequired,
    cancelReferral: PropTypes.func.isRequired,
    exportReferralLedger: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    referralLedger: PropTypes.array,
    stats: PropTypes.object,
    pagination: PropTypes.object
};

const mapStateToProps = state => ({
    loading: state.referralLedger.loading,
    referralLedger: state.referralLedger.referralLedger,
    stats: state.referralLedger.stats,
    pagination: state.referralLedger.pagination
});

export default connect(mapStateToProps, { 
    getAllReferralLedger, 
    getReferralStats, 
    deleteReferralLedger,
    completeReferral,
    cancelReferral,
    exportReferralLedger
})(ReferralLedgers);
